/**
 * Templates Page
 * Tab 1 — Browse: select a template and create a dashboard from it.
 * Tab 2 — Manage: admin CRUD for creating, editing and deleting templates.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  LayoutTemplate,
  Plus,
  AlertCircle,
  Trash2,
  Edit3,
  Save,
  Loader2,
  Copy,
  Info,
  Settings2,
  Grid3X3,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
} from 'lucide-react';
import { Layout } from '../components/layout';
import { Modal } from '../components/ui';
import { templatesApi } from '../api/client';
import type { DashboardTemplate } from '../types';
import { useAuth } from '../contexts';

type TabId = 'browse' | 'manage';

// ─── Browse Tab ──────────────────────────────────────────────────

function BrowseTab({
  templates,
  isLoading,
  isError,
}: {
  templates: DashboardTemplate[] | undefined;
  isLoading: boolean;
  isError: boolean;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<DashboardTemplate | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newDashboardName, setNewDashboardName] = useState('');

  const filteredTemplates = templates?.filter(
    (t) =>
      t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.category.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const createDashboardMutation = useMutation({
    mutationFn: ({ id, name }: { id: number; name: string }) =>
      templatesApi.createDashboard(id, name),
    onSuccess: (dashboard) => {
      queryClient.invalidateQueries({ queryKey: ['dashboards'] });
      setIsModalOpen(false);
      setSelectedTemplate(null);
      setNewDashboardName('');
      navigate(`/dashboards/${dashboard.id}`);
    },
  });

  const handleUseTemplate = (template: DashboardTemplate) => {
    setSelectedTemplate(template);
    setNewDashboardName(template.name);
    setIsModalOpen(true);
  };

  const handleCreateDashboard = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedTemplate) {
      createDashboardMutation.mutate({ id: selectedTemplate.id, name: newDashboardName });
    }
  };

  const templatesByCategory = filteredTemplates?.reduce(
    (acc, template) => {
      const category = template.category || 'General';
      if (!acc[category]) acc[category] = [];
      acc[category].push(template);
      return acc;
    },
    {} as Record<string, DashboardTemplate[]>,
  );

  return (
    <>
      {/* Search */}
      <div className="mb-6">
        <input
          type="text"
          placeholder="Search templates..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full max-w-sm px-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#45bdd6]/20 focus:border-[#45bdd6]"
        />
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden animate-pulse">
              <div className="h-40 bg-gray-200" />
              <div className="p-4">
                <div className="h-5 bg-gray-200 rounded w-2/3 mb-2" />
                <div className="h-4 bg-gray-100 rounded w-full" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Error */}
      {isError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
          <p className="text-red-600">Failed to load templates.</p>
        </div>
      )}

      {/* Empty */}
      {!isLoading && !isError && filteredTemplates?.length === 0 && (
        <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
          <LayoutTemplate className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No templates available</h3>
          <p className="text-gray-500">
            Switch to the <strong>Manage</strong> tab to create your first template.
          </p>
        </div>
      )}

      {/* Templates by Category */}
      {!isLoading && !isError && templatesByCategory && Object.keys(templatesByCategory).length > 0 && (
        <div className="space-y-8">
          {Object.entries(templatesByCategory).map(([category, categoryTemplates]) => (
            <div key={category}>
              <h2 className="text-lg font-medium text-gray-900 mb-4">{category}</h2>
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {categoryTemplates.map((template) => (
                  <div
                    key={template.id}
                    className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow group"
                  >
                    {/* Preview Image */}
                    <div className="h-40 bg-gradient-to-br from-blue-50 to-purple-50 relative">
                      {template.preview_image ? (
                        <img src={template.preview_image} alt={template.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="flex items-center justify-center h-full">
                          <LayoutTemplate className="w-16 h-16 text-blue-200" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleUseTemplate(template)}
                          className="px-4 py-2 bg-[#45bdd6] text-white rounded-lg hover:bg-[#3aa8bf] flex items-center gap-2"
                        >
                          <Plus className="w-4 h-4" />
                          Use Template
                        </button>
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="font-medium text-gray-900 mb-1">{template.name}</h3>
                      <p className="text-sm text-gray-500 line-clamp-2">{template.description}</p>
                      {template.required_endpoints && template.required_endpoints.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-100">
                          <p className="text-xs text-gray-400">Requires: {template.required_endpoints.join(', ')}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Dashboard Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setSelectedTemplate(null); setNewDashboardName(''); }}
        title="Create Dashboard from Template"
      >
        {selectedTemplate && (
          <form onSubmit={handleCreateDashboard} className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <LayoutTemplate className="w-6 h-6 text-[#45bdd6]" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">{selectedTemplate.name}</h4>
                  <p className="text-sm text-gray-500">{selectedTemplate.category}</p>
                </div>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Dashboard Name *</label>
              <input
                type="text"
                value={newDashboardName}
                onChange={(e) => setNewDashboardName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#45bdd6] focus:border-blue-500"
                placeholder="My New Dashboard"
                required
              />
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={() => { setIsModalOpen(false); setSelectedTemplate(null); setNewDashboardName(''); }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-[#45bdd6] text-white rounded-lg hover:bg-[#3aa8bf] transition-colors disabled:opacity-50"
                disabled={createDashboardMutation.isPending}
              >
                {createDashboardMutation.isPending ? 'Creating...' : 'Create Dashboard'}
              </button>
            </div>
          </form>
        )}
      </Modal>
    </>
  );
}

// ─── Widget summary helper ───────────────────────────────────────

function widgetSummary(config: DashboardTemplate['template_config']): string {
  const widgets = config?.widgets;
  if (!widgets || widgets.length === 0) return 'No widgets';
  const counts: Record<string, number> = {};
  for (const w of widgets) {
    const t = w.type || 'unknown';
    counts[t] = (counts[t] || 0) + 1;
  }
  return Object.entries(counts).map(([t, c]) => `${c}× ${t}`).join(', ');
}

// ─── Manage Tab ──────────────────────────────────────────────────

const CATEGORY_OPTIONS = [
  'Production', 'Quality', 'Logistics', 'Maintenance',
  'Finance', 'Planning', 'General',
];

function ManageTab({
  templates,
  isLoading,
  isError,
}: {
  templates: DashboardTemplate[] | undefined;
  isLoading: boolean;
  isError: boolean;
}) {
  const queryClient = useQueryClient();

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);

  // Form fields
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('General');
  const [requiredEndpoints, setRequiredEndpoints] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(60);

  // Expanded row for config preview
  const [expandedId, setExpandedId] = useState<number | null>(null);

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: Partial<DashboardTemplate>) => templatesApi.create(data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['templates'] }); closeModal(); },
  });
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<DashboardTemplate> }) => templatesApi.update(id, data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['templates'] }); closeModal(); },
  });
  const deleteMutation = useMutation({
    mutationFn: (id: number) => templatesApi.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['templates'] }),
  });

  const resetForm = () => {
    setEditId(null);
    setName('');
    setDescription('');
    setCategory('General');
    setRequiredEndpoints('');
    setAutoRefresh(true);
    setRefreshInterval(60);
  };
  const closeModal = () => { setModalOpen(false); resetForm(); };

  const openCreate = () => { resetForm(); setModalOpen(true); };

  const openEdit = (t: DashboardTemplate) => {
    setEditId(t.id);
    setName(t.name);
    setDescription(t.description);
    setCategory(t.category || 'General');
    setRequiredEndpoints((t.required_endpoints || []).join(', '));
    setAutoRefresh(t.template_config?.auto_refresh ?? true);
    setRefreshInterval(t.template_config?.refresh_interval_seconds ?? 60);
    setModalOpen(true);
  };

  const handleDuplicate = (t: DashboardTemplate) => {
    resetForm();
    setName(`${t.name} (Copy)`);
    setDescription(t.description);
    setCategory(t.category || 'General');
    setRequiredEndpoints((t.required_endpoints || []).join(', '));
    setAutoRefresh(t.template_config?.auto_refresh ?? true);
    setRefreshInterval(t.template_config?.refresh_interval_seconds ?? 60);
    setModalOpen(true);
  };

  const handleDelete = (t: DashboardTemplate) => {
    if (confirm(`Delete template "${t.name}"? This cannot be undone.`)) {
      deleteMutation.mutate(t.id);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const endpoints = requiredEndpoints
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

    const payload: Partial<DashboardTemplate> = {
      name,
      description,
      category,
      required_endpoints: endpoints,
      template_config: {
        description,
        layout: [],
        auto_refresh: autoRefresh,
        refresh_interval_seconds: refreshInterval,
        widgets: [],
      },
    };

    // If editing, preserve existing layout/widgets from the original template
    if (editId !== null) {
      const existing = templates?.find((t) => t.id === editId);
      if (existing?.template_config) {
        payload.template_config = {
          ...existing.template_config,
          description,
          auto_refresh: autoRefresh,
          refresh_interval_seconds: refreshInterval,
        };
      }
      updateMutation.mutate({ id: editId, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">
          Create and manage dashboard templates. Templates define a reusable layout
          with pre-configured widgets that users can instantiate as new dashboards.
        </p>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2 bg-[#45bdd6] text-white rounded-lg hover:bg-[#3ba8c0] text-sm font-medium transition-colors whitespace-nowrap ml-4"
        >
          <Plus className="w-4 h-4" />
          New Template
        </button>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="animate-pulse space-y-4">
          {[...Array(3)].map((_, i) => <div key={i} className="h-20 bg-gray-100 rounded-lg" />)}
        </div>
      )}

      {/* Error */}
      {isError && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
          <p className="text-sm text-red-700">Failed to load templates.</p>
        </div>
      )}

      {/* Empty */}
      {!isLoading && !isError && templates?.length === 0 && (
        <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-xl p-10 text-center">
          <LayoutTemplate className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No templates yet</h3>
          <p className="text-sm text-gray-500 mb-4">Create your first dashboard template to get started.</p>
          <button
            onClick={openCreate}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#45bdd6] text-white rounded-lg hover:bg-[#3ba8c0] text-sm font-medium"
          >
            <Plus className="w-4 h-4" />
            Create First Template
          </button>
        </div>
      )}

      {/* Template List */}
      {!isLoading && !isError && templates && templates.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {/* Table header */}
          <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/50 grid grid-cols-12 gap-4 text-xs font-semibold text-gray-500 uppercase">
            <span className="col-span-4">Template</span>
            <span className="col-span-2">Category</span>
            <span className="col-span-2">Widgets</span>
            <span className="col-span-2">Refresh</span>
            <span className="col-span-2 text-right">Actions</span>
          </div>

          {templates.map((t) => (
            <div key={t.id}>
              <div className="grid grid-cols-12 gap-4 px-5 py-4 border-b border-gray-50 hover:bg-gray-50/50 transition-colors items-center group">
                {/* Name & Description */}
                <div className="col-span-4">
                  <p className="font-medium text-gray-900 text-sm">{t.name}</p>
                  {t.description && <p className="text-xs text-gray-400 mt-0.5 truncate">{t.description}</p>}
                </div>

                {/* Category */}
                <div className="col-span-2">
                  <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                    {t.category || 'General'}
                  </span>
                </div>

                {/* Widgets summary */}
                <div className="col-span-2 text-xs text-gray-500">
                  {widgetSummary(t.template_config)}
                </div>

                {/* Refresh */}
                <div className="col-span-2 text-xs text-gray-500">
                  {t.template_config?.auto_refresh
                    ? `${t.template_config.refresh_interval_seconds ?? 60}s`
                    : 'Off'}
                </div>

                {/* Actions */}
                <div className="col-span-2 flex items-center justify-end gap-1">
                  <button
                    onClick={() => setExpandedId(expandedId === t.id ? null : t.id)}
                    className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                    title="Details"
                  >
                    {expandedId === t.id
                      ? <ChevronUp className="w-4 h-4" />
                      : <ChevronDown className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => handleDuplicate(t)}
                    className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                    title="Duplicate"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => openEdit(t)}
                    className="p-1.5 text-gray-400 hover:text-[#45bdd6] hover:bg-[#45bdd6]/10 rounded transition-colors"
                    title="Edit"
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(t)}
                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Expanded detail */}
              {expandedId === t.id && (
                <div className="px-5 py-3 bg-gray-50 border-b border-gray-200 text-xs">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="font-medium text-gray-700 mb-1">Required Endpoints</p>
                      {t.required_endpoints && t.required_endpoints.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {t.required_endpoints.map((ep, i) => (
                            <code key={i} className="bg-white px-1.5 py-0.5 rounded border text-gray-600">{ep}</code>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-400 italic">None specified</p>
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-gray-700 mb-1">Layout ({t.template_config?.layout?.length ?? 0} positions)</p>
                      <p className="font-medium text-gray-700 mt-2 mb-1">Widgets ({t.template_config?.widgets?.length ?? 0})</p>
                      <div className="flex flex-wrap gap-1">
                        {t.template_config?.widgets?.map((w, i) => (
                          <span key={i} className="inline-flex items-center gap-1 bg-white px-1.5 py-0.5 rounded border text-gray-600">
                            <Grid3X3 className="w-3 h-3" /> {w.name} ({w.type})
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  {t.created_at && (
                    <p className="text-gray-400 mt-2">
                      Created: {new Date(t.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
        <Info className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-sm font-medium text-blue-800">How Templates Work</p>
          <p className="text-xs text-blue-600 mt-1">
            Templates store a complete dashboard configuration (layout grid, widgets, refresh settings).
            When a user clicks "Use Template" on the Browse tab, a new dashboard is created as a copy — the original template stays unchanged.
            Widgets and layout positions from the template are cloned into the new dashboard.
          </p>
        </div>
      </div>

      {/* ─── Create / Edit Modal ─── */}
      <Modal
        isOpen={modalOpen}
        onClose={closeModal}
        title={editId !== null ? 'Edit Template' : 'New Template'}
        size="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Production Dashboard"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#45bdd6]/20 focus:border-[#45bdd6]"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="Brief description of what this template provides"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#45bdd6]/20 focus:border-[#45bdd6]"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#45bdd6]/20 focus:border-[#45bdd6]"
              >
                {CATEGORY_OPTIONS.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Required Endpoints</label>
              <input
                type="text"
                value={requiredEndpoints}
                onChange={(e) => setRequiredEndpoints(e.target.value)}
                placeholder="e.g. Orders, Operations"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-[#45bdd6]/20 focus:border-[#45bdd6]"
              />
              <p className="text-[10px] text-gray-400 mt-0.5">Comma-separated OData entity set names</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                  className="w-4 h-4 text-[#45bdd6] border-gray-300 rounded focus:ring-[#45bdd6]"
                />
                Auto-refresh enabled
              </label>
            </div>
            {autoRefresh && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Refresh interval (seconds)</label>
                <input
                  type="number"
                  value={refreshInterval}
                  onChange={(e) => setRefreshInterval(Number(e.target.value))}
                  min={5}
                  max={3600}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#45bdd6]/20 focus:border-[#45bdd6]"
                />
              </div>
            )}
          </div>

          {/* Error */}
          {(createMutation.isError || updateMutation.isError) && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5" />
              <p className="text-sm text-red-700">Error saving template. Check inputs and backend.</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2 border-t border-gray-200">
            <button
              type="button"
              onClick={closeModal}
              className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPending}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                isPending
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-[#45bdd6] text-white hover:bg-[#3ba8c0]'
              }`}
            >
              {isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {editId !== null ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────

export function TemplatesPage() {
  const { isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState<TabId>('browse');

  const { data: templates, isLoading, isError } = useQuery({
    queryKey: ['templates'],
    queryFn: templatesApi.list,
  });

  return (
    <Layout title="Templates">
      <div className="p-6">
        {/* Header with Tabs */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Dashboard Templates</h1>
            <p className="text-gray-500 mt-1">
              {activeTab === 'browse'
                ? 'Select a template to create a new dashboard'
                : 'Create and manage dashboard templates'}
            </p>
          </div>
        </div>

        {/* Tab bar */}
        <div className="flex gap-1 mb-6 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('browse')}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'browse'
                ? 'border-[#45bdd6] text-[#45bdd6]'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <LayoutTemplate className="w-4 h-4" />
            Browse
          </button>
          {isAdmin && (
            <button
              onClick={() => setActiveTab('manage')}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'manage'
                  ? 'border-[#45bdd6] text-[#45bdd6]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Settings2 className="w-4 h-4" />
              Manage
            </button>
          )}
        </div>

        {/* Tab Content */}
        {activeTab === 'browse' && (
          <BrowseTab templates={templates} isLoading={isLoading} isError={isError} />
        )}
        {activeTab === 'manage' && isAdmin && (
          <ManageTab templates={templates} isLoading={isLoading} isError={isError} />
        )}
      </div>
    </Layout>
  );
}
